/*!
 * Copyright © 2018-2019. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { isElementNull } from '../../../../utils';
import { ICoordinates, IWorkOrder } from '../../../core/models';
import { SaveWorkOrder } from '../../../core/store/actions';
import { getHasEditWorkOrderAnError, getIsEditWorkOrderSaving, getWorkOrderToEdit } from '../../../core/store/selectors';
import { ESearchEntityType, ISearchLightResult } from '../../../searchlight';

@Component({
  selector: 'fm-edit-work-order',
  templateUrl: './edit-work-order.component.html',
  styleUrls: ['./edit-work-order.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditWorkOrderComponent implements OnInit, OnDestroy {
  public hasError$: Observable<boolean>;
  public isSaving$: Observable<boolean>;
  public workOrder$: Observable<IWorkOrder>;

  public form: UntypedFormGroup;
  public initialLocationValue$: Observable<string>;
  public isSubmitted: boolean;
  public mapLocation: ICoordinates;
  // eslint-disable-next-line no-bitwise
  public searchLightTypes = ESearchEntityType.Address | ESearchEntityType.Place;

  private readonly _unsubscribe$ = new Subject<void>();
  private _workOrder: IWorkOrder;

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _store: Store,
    private readonly _dialog: MatLegacyDialog
  ) {}

  public ngOnInit(): void {
    this.hasError$ = this._store.pipe(select(getHasEditWorkOrderAnError));
    this.isSaving$ = this._store.pipe(select(getIsEditWorkOrderSaving));
    this.workOrder$ = this._store.pipe(select(getWorkOrderToEdit));

    this.workOrder$.pipe(takeUntil(this._unsubscribe$), take(1)).subscribe(workOrder => {
      this._workOrder = workOrder;
    });

    this.initialLocationValue$ = this.workOrder$.pipe(
      take(1),
      filter(workOrder => !!workOrder),
      map(workOrder => workOrder.displayAddress)
    );
    this._initializeForm();
    this._updateMap();
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public cancel(): void {
    this._dialog.closeAll();
  }

  public onLocationSearchTextChange(): void {
    this.form.get('location').setValue(null);
  }

  public save(): void {
    this.isSubmitted = true;
    if (this.form.invalid) {
      return;
    }
    this._store.dispatch(new SaveWorkOrder(this._workOrder));
  }

  private _initializeForm(): void {
    const initialSearchLightResult = <ISearchLightResult>{
      lat: this._workOrder.lat,
      lng: this._workOrder.lng,
      text: this._workOrder.address1
    };

    this.form = this._formBuilder.group({
      location: [initialSearchLightResult, this._locationValidator],
      address: [{ value: this._workOrder.address1, disabled: true }],
      customer: [{ value: this._workOrder.customer, disabled: true }],
      phone: [{ value: this._workOrder.phone, disabled: true }],
      reason: [{ value: this._workOrder.description, disabled: true }],
      durationInHours: [{ value: this._workOrder.durationInHours, disabled: true }],
      durationInMinutes: [{ value: this._workOrder.durationInMinutes, disabled: true }],
      workOrderId: [{ value: this._workOrder.clientWorkOrderId, disabled: true }],
      scheduledDateTime: [{ value: this._workOrder.scheduledDateTime, disabled: true }],
      type: [{ value: this._workOrder.type, disabled: true }]
    });

    this._setupFormListeners();
  }

  private _setupFormListeners(): void {
    this.form
      .get('location')
      .valueChanges.pipe(takeUntil(this._unsubscribe$))
      .subscribe((result: ISearchLightResult) => {
        if (!isElementNull(result) && !isElementNull(result.lat)) {
          this._workOrder.displayAddress = result.text;
          this._workOrder.lat = result.lat;
          this._workOrder.lng = result.lng;

          if (result.type !== ESearchEntityType.Place) {
            this._workOrder.address1 = result.text;
            this._workOrder.address2 = result.addressComponents;
          } else {
            this._workOrder.address1 = null;
            this._workOrder.address2 = null;
          }

          this.form.get('address').setValue(result.text);
          this._updateMap();
        }
      });
  }

  private _updateMap(): void {
    this.mapLocation = { Latitude: this._workOrder.lat || 0, Longitude: this._workOrder.lng || 0 };
  }

  private readonly _locationValidator = (control: AbstractControl) =>
    isElementNull(control.value) || isElementNull(control.value.lat) ? { location: { value: <ISearchLightResult>null } } : null;
}
